import { useEffect, useRef } from "react";
import { AttachmentIcon, CloseIcon } from "vega-icons";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useUploadUnlinkedAttachment } from "@vega-api/generated/lp-portal-attachment-v1/lp-portal-attachment-v1";
import { InteractiveButton } from "@vega-core/components/Buttons/InteractiveButton";
import { useToast } from "@vega-core/components/Toast/useToast";
import { TruncateText } from "@vega-core/components/TruncateText/TruncateText";
import { m } from "@vega-core/paraglide/messages";

export type AttachmentItem = {
  id: string;
  file?: File;
  attachment?: {
    id: string;
    fileName: string;
    size: number;
  };
  isUploading?: boolean;
  isError?: boolean;
};

type FileAttachmentItemProps = {
  item: AttachmentItem;
  onUploadSuccess: (
    itemId: string,
    attachment: { id: string; fileName: string; size: number },
  ) => void;
  onUploadStateChange: (
    itemId: string,
    isUploading: boolean,
    isError: boolean,
  ) => void;
  onRemove: (itemId: string) => void;
};

const UploadingAttachmentItem = ({ fileName }: { fileName: string }) => (
  <div className="flex items-center justify-between rounded-md px-3 py-2 border border-border/primary bg-background/secondary">
    <div className="flex items-center min-w-0 flex-1 gap-2">
      <div className="flex-shrink-0">
        <AttachmentIcon className="size-4 text-icon/primary" />
      </div>

      <div className="flex items-center min-w-0 flex-1">
        <span className="body-sm-regular text-text/primary opacity-30">
          <TruncateText limit={15} text={fileName} tooltip={fileName} />
        </span>
      </div>
    </div>

    <div className="flex items-center gap-1 ml-2">
      <span className="text-brand-default body-sm-regular">
        {m.subscription_dropFile_uploading()}
      </span>
    </div>
  </div>
);

const ErrorAttachmentItem = ({
  fileName,
  itemId,
  onRemove,
  onRetry,
}: {
  fileName: string;
  itemId: string;
  onRemove: (itemId: string) => void;
  onRetry: () => void;
}) => (
  <div className="flex items-center justify-between rounded-md px-3 py-2 border border-border/primary bg-background/secondary">
    <div className="flex items-center min-w-0 flex-1 gap-2">
      <div className="flex-shrink-0">
        <CloseIcon className="size-4 text-red-600" />
      </div>

      <div className="flex items-center min-w-0 flex-1">
        <span className="body-sm-regular text-error/foreground">
          <TruncateText limit={18} text={fileName} tooltip={fileName} />
        </span>
      </div>

      <div className="flex-shrink-0">
        <span className="text-error/foreground body-sm-regular">
          {m.errors_uploadFailed()}
        </span>
      </div>
    </div>

    <div className="flex items-center gap-1 ml-2">
      <InteractiveButton
        ariaLabel={m.btn_tryAgain()}
        dataTestId={`retry-attachment-${itemId}`}
        onClick={onRetry}
        size="small"
        variant="text"
      >
        {m.btn_tryAgain()}
      </InteractiveButton>

      <InteractiveButton
        ariaLabel={m.common_remove()}
        dataTestId={`remove-attachment-${itemId}`}
        onClick={() => onRemove(itemId)}
        variant="text"
      >
        <CloseIcon className="text-icon/primary size-4" />
      </InteractiveButton>
    </div>
  </div>
);

const UploadedAttachmentItem = ({
  fileName,
  fileSize,
  itemId,
  onRemove,
}: {
  fileName: string;
  fileSize: number;
  itemId: string;
  onRemove: (itemId: string) => void;
}) => (
  <div className="flex items-center justify-between rounded-md px-3 py-2 border border-border/primary bg-background/secondary">
    <div className="flex items-center min-w-0 flex-1 gap-2">
      <div className="flex-shrink-0">
        <AttachmentIcon className="size-4 text-icon/primary" />
      </div>

      <div className="flex items-center min-w-0 flex-1">
        <span className="body-sm-regular text-text/primary">
          <TruncateText limit={30} text={fileName} tooltip={fileName} />
        </span>
        <span className="text-text/secondary body-sm-regular ml-2">
          {m.fileSize_kilobytes({
            size: `${(fileSize / 1024).toFixed(1)}`,
          })}
        </span>
      </div>
    </div>

    <div className="flex items-center gap-1 ml-2">
      <InteractiveButton
        ariaLabel={m.common_remove()}
        dataTestId={`remove-attachment-${itemId}`}
        onClick={() => onRemove(itemId)}
        variant="text"
      >
        <CloseIcon className="text-icon/primary size-4" />
      </InteractiveButton>
    </div>
  </div>
);

export const FileAttachmentItem = ({
  item,
  onUploadSuccess,
  onUploadStateChange,
  onRemove,
}: FileAttachmentItemProps) => {
  const showToast = useToast();
  const fileName = item.attachment?.fileName || item.file?.name || "";
  const fileSize = item.attachment?.size || item.file?.size || 0;
  const uploadInitiatedRef = useRef(false);

  const {
    mutate: uploadMutation,
    isPending,
    isSuccess,
    isError,
  } = useUploadUnlinkedAttachment({
    mutation: {
      onSuccess: (response) => {
        if (response.data && item.file) {
          const attachment = {
            id: response.data.id,
            fileName: response.data.fileName,
            size: item.file.size,
          };
          onUploadSuccess(item.id, attachment);
        }
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error, {
          defaultTitle: m.common_uploadFailedTitle(),
          defaultMessage: m.errors_uploadFailedWithFile({
            fileName: item.file?.name || "",
          }),
        });
        showToast({
          title: errorMessage.title,
          description: errorMessage.description,
          variant: "destructive",
        });
      },
    },
  });

  useEffect(() => {
    if (item.file && !item.attachment && !uploadInitiatedRef.current) {
      uploadInitiatedRef.current = true;
      uploadMutation({
        data: { file: item.file },
      });
    }
  }, [item.file, item.attachment, uploadMutation]);

  useEffect(() => {
    if (isSuccess || isError) {
      uploadInitiatedRef.current = false;
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    onUploadStateChange(item.id, isPending, isError);
  }, [item.id, isPending, isError, onUploadStateChange]);

  const handleRetry = () => {
    if (item.file) {
      uploadMutation({
        data: { file: item.file },
      });
    }
  };

  if (isError) {
    return (
      <ErrorAttachmentItem
        fileName={fileName}
        itemId={item.id}
        onRemove={onRemove}
        onRetry={handleRetry}
      />
    );
  }

  if (isPending) {
    return <UploadingAttachmentItem fileName={fileName} />;
  }

  return (
    <UploadedAttachmentItem
      fileName={fileName}
      fileSize={fileSize}
      itemId={item.id}
      onRemove={onRemove}
    />
  );
};
